import React, { useState } from "react";
import axios from "axios";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  city: "",
  state: "",
  message: "",
};

const InquiryModal = ({ isOpen, onClose, services = [] }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetAndClose = () => {
    setForm(initialForm);
    setError("");
    setSuccess("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      setError("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/inquiry/create-inquiry`,
        form
      );
      setSuccess("Your inquiry has been submitted successfully!");
      setForm(initialForm);
      setTimeout(() => {
        resetAndClose();
      }, 1500);
    } catch (err) {
      console.error("Error submitting inquiry:", err);
      setError(
        err?.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={resetAndClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6 relative">
          <button
            onClick={resetAndClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-[#101828] text-2xl leading-none"
            aria-label="Close inquiry modal"
          >
            &times;
          </button>

          <h2 className="text-2xl font-extrabold text-[#101828] mb-1">
            Send an Inquiry
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Fill in your details and our team will get back to you shortly.
          </p>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-2 mb-3">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 text-green-600 text-sm rounded-lg px-4 py-2 mb-3">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-[#101828]">Name *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-[#101828]">Email *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-[#101828]">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                placeholder="Phone number"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-[#101828]">Service *</label>
              <select
                name="service"
                value={form.service}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6] bg-white"
              >
                <option value="">Select a service</option>
                {services.length > 0 ? (
                  services.map((s) => (
                    <option key={s._id} value={s.serviceName}>
                      {s.serviceName}
                    </option>
                  ))
                ) : (
                  <option value="General Inquiry">General Inquiry</option>
                )}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-[#101828]">City</label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                placeholder="City"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-[#101828]">State</label>
              <input
                type="text"
                name="state"
                value={form.state}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                placeholder="State"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-[#101828]">Message *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                placeholder="Tell us what you need..."
              />
            </div>

            <div className="sm:col-span-2 flex justify-end gap-3 mt-2">
              <button
                type="button"
                onClick={resetAndClose}
                className="px-6 py-2 rounded-full font-semibold text-[#101828] border border-gray-200 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 rounded-full font-semibold text-white bg-[#2F9BD6] hover:bg-[#218ac0] transition disabled:opacity-60"
              >
                {loading ? "Sending..." : "Submit Inquiry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default InquiryModal;